// R-131-FIN-DECISION-K — stale-pin detection for pinned pathway comparisons vs BoM / FA edits.

import type { SiteEntity } from '../types/site';
import type { SiteRagReport } from '../types/siteRag';

export interface PathwayComparisonFreshness {
  stale: boolean;
  staleDays?: number;
  comparisonAtLabel?: string;
  inputsAtLabel?: string;
}

function toEpoch(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function latestInputEditEpoch(
  site: Pick<SiteEntity, 'supplyChainPlan' | 'financialAssumptions'> | null | undefined,
): number | null {
  const bomAt = toEpoch(site?.supplyChainPlan?.updatedAt);
  const faAt = toEpoch(site?.financialAssumptions?.updatedAt);
  if (bomAt == null && faAt == null) return null;
  if (bomAt == null) return faAt;
  if (faAt == null) return bomAt;
  return Math.max(bomAt, faAt);
}

/**
 * Mark reports stale when live BoM / FA inputs changed after the pinned
 * comparison (or gen-time snapshot) was last updated.
 */
export function getPathwayComparisonFreshness(
  report: SiteRagReport | undefined,
  site: Pick<SiteEntity, 'supplyChainPlan' | 'financialAssumptions' | 'pinnedPathwayComparisonId'> | null | undefined,
  liveComparisonUpdatedAt?: string | null,
): PathwayComparisonFreshness {
  const atGen = report?.pathwayComparisonAtGen;
  if (!atGen) return { stale: false };

  const generatedAt = toEpoch(atGen.updatedAt);
  const liveAt = toEpoch(liveComparisonUpdatedAt ?? atGen.updatedAt);
  const inputsAt = latestInputEditEpoch(site);

  if (generatedAt == null) return { stale: false };

  const referenceAt = inputsAt ?? liveAt;
  if (referenceAt == null || referenceAt <= generatedAt) {
    return { stale: false };
  }

  const staleMs = referenceAt - generatedAt;
  const staleDays = Math.max(1, Math.ceil(staleMs / (24 * 60 * 60 * 1000)));

  return {
    stale: true,
    staleDays,
    comparisonAtLabel: atGen.updatedAt,
    inputsAtLabel: inputsAt != null
      ? new Date(referenceAt).toISOString()
      : liveComparisonUpdatedAt ?? undefined,
  };
}
